import { TrackedBookingLink } from "@/components/TrackedBookingLink";
import { Phone, ChevronDown, Handshake } from "lucide-react";
import { motion } from "framer-motion";

export function SplitHero() {
  return (
    <section className="relative min-h-[90vh]">
      {/* Desktop: CSS Grid with shared rows so headings align structurally */}
      <div className="hidden md:grid md:grid-cols-2 min-h-[90vh]">
        {/* ── Left panel background ── */}
        <div className="relative bg-foreground text-background dot-pattern col-start-1 row-start-1 row-span-3">
          <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
            <div className="absolute -right-32 -top-32 h-[400px] w-[400px] rounded-full gradient-orange blur-3xl" />
          </div>
        </div>
        {/* ── Right panel background ── */}
        <div className="relative gradient-orange shimmer text-primary-foreground col-start-2 row-start-1 row-span-3">
          <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
            <div className="absolute -left-32 -bottom-32 h-[400px] w-[400px] rounded-full bg-foreground blur-3xl" />
          </div>
        </div>

        {/* ── Row 1: Eyebrow row (fixed height, both sides) ── */}
        {/* Left eyebrow */}
        <div className="relative z-10 col-start-1 row-start-1 flex items-end px-16 lg:px-24 pt-[20vh] pb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            Infinity Financial Solutions
          </p>
        </div>
        {/* Right eyebrow – empty but same height */}
        <div className="relative z-10 col-start-2 row-start-1 px-16 lg:px-24 pt-[20vh] pb-4" />

        {/* ── Row 2: Headlines (both start at exact same line) ── */}
        {/* Left headline */}
        <div className="relative z-10 col-start-1 row-start-2 px-16 lg:px-24">
          <h1 className="font-display text-5xl font-extrabold leading-[1.1] tracking-tight text-background md:text-6xl lg:text-7xl">
            Your finance
            <br />
            team, reimagined.
          </h1>
        </div>
        {/* Right headline */}
        <div className="relative z-10 col-start-2 row-start-2 px-16 lg:px-24">
          <h2 className="font-display text-5xl font-extrabold leading-[1.1] tracking-tight text-primary-foreground md:text-6xl lg:text-7xl">
            Support that
            <br />
            grows with you.
          </h2>
        </div>

        {/* ── Row 3: Supporting content ── */}
        {/* Left content */}
        <div className="relative z-10 col-start-1 row-start-3 px-16 lg:px-24 pt-10 pb-24">
          <div className="flex flex-wrap gap-4">
            <TrackedBookingLink className="gradient-orange border-0 text-base font-semibold hover-glow">
Book a Free Discovery Call <Phone className="ml-2 h-4 w-4" />
              </TrackedBookingLink>
              <button
                onClick={() => {
                  document.getElementById('scaling-solution')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center rounded-md text-base h-11 px-8 border border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 transition-colors cursor-pointer"
              >
                How We Work <Handshake className="ml-2 h-4 w-4" />
              </button>
          </div>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-background/50">
            AAT Licensed · Xero Gold Partner · 200+ clients · Rated 5★
          </p>
        </div>
        {/* Right content */}
        <div className="relative z-10 col-start-2 row-start-3 px-16 lg:px-24 pt-6 pb-24">
          <p className="max-w-md text-lg leading-relaxed text-primary-foreground/80">
            From simple bookkeeping to a fully outsourced finance function, we work hand-in-hand with you at every stage.
          </p>
        </div>
      </div>

      {/* ── Mobile: simple stacked layout ── */}
      <div className="md:hidden">
        <div className="relative flex flex-col justify-center bg-foreground text-background px-8 py-16 dot-pattern">
          <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
            <div className="absolute -right-32 -top-32 h-[400px] w-[400px] rounded-full gradient-orange blur-3xl" />
          </div>
          <div className="relative">
            <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Infinity Financial Solutions
            </p>
            <h1 className="font-display text-5xl font-extrabold leading-[1.1] tracking-tight">
              Your finance
              <br />
              team, reimagined.
            </h1>
            <div className="mt-10 flex flex-wrap gap-4">
              <TrackedBookingLink className="gradient-orange border-0 text-base font-semibold hover-glow">
                Book a Discovery Call <Phone className="ml-2 h-4 w-4" />
              </TrackedBookingLink>
              <button
                onClick={() => {
                  document.getElementById('scaling-solution')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center rounded-md text-base h-11 px-8 border border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 transition-colors cursor-pointer"
              >
                How We Work <Handshake className="ml-2 h-4 w-4" />
              </button>
            </div>
            <p className="mt-6 text-[10px] uppercase tracking-[0.2em] text-background/50">
              AAT Licensed · Xero Gold Partner · 200+ clients · 5★
            </p>
          </div>
        </div>
        <div className="relative gradient-orange shimmer text-primary-foreground px-8 py-16">
          <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
            <div className="absolute -left-32 -bottom-32 h-[400px] w-[400px] rounded-full bg-foreground blur-3xl" />
          </div>
          <div className="relative">
            <h2 className="font-display text-5xl font-extrabold leading-[1.1] tracking-tight">
              Support that
              <br />
              grows with you.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed opacity-80">
              From simple bookkeeping to a fully outsourced finance function, we work hand-in-hand with you at every stage.
            </p>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 rounded-full bg-foreground/70 px-3 py-2 text-background z-10 cursor-pointer border-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        onClick={() => {
          const next = document.querySelector('section + section');
          next?.scrollIntoView({ behavior: 'smooth' });
        }}
        aria-label="Scroll to next section"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] font-medium">Scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce-subtle" />
      </motion.button>
    </section>
  );
}
